<script setup>
import { computed, watch, onMounted, ref } from 'vue';
import {
  useMapGetter,
  useFunctionGetter,
  useStore,
} from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import ContactConversations from './ContactConversations.vue';
import ConversationAction from './ConversationAction.vue';
import ConversationParticipant from './ConversationParticipant.vue';
import ContactInfo from './contact/ContactInfo.vue';
import ContactEmbedPanel from './contact/ContactEmbedPanel.vue';
import ContactAddTicketEmbedPanel from './contact/ContactAddTicketEmbedPanel.vue';
import ContactOrderEmbedPanel from './contact/ContactOrderEmbedPanel.vue';
import ContactTicketDetailEmbedPanel from './contact/ContactTicketDetailEmbedPanel.vue';
import ContactUserIdEmbedPanel from './contact/ContactUserIdEmbedPanel.vue';
import ContactNotes from './contact/ContactNotes.vue';
import ConversationInfo from './ConversationInfo.vue';
import CustomAttributes from './customAttributes/CustomAttributes.vue';
import Draggable from 'vuedraggable';
import MacrosList from './Macros/List.vue';
import ShopifyOrdersList from 'dashboard/components/widgets/conversation/ShopifyOrdersList.vue';
import SidebarActionsHeader from 'dashboard/components-next/SidebarActionsHeader.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import LinearIssuesList from 'dashboard/components/widgets/conversation/linear/IssuesList.vue';
import LinearSetupCTA from 'dashboard/components/widgets/conversation/linear/LinearSetupCTA.vue';
import { useConversationSidebars } from 'dashboard/composables/useConversationSidebars';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  inboxId: {
    type: Number,
    default: undefined,
  },
  onResizeStart: {
    type: Function,
    default: null,
  },
});

const {
  updateUISettings,
  isContactSidebarItemOpen,
  conversationSidebarItemsOrder,
  toggleSidebarUIState,
} = useUISettings();
const { closeContactSidebar } = useConversationSidebars();

const dragging = ref(false);
const conversationSidebarItems = ref([]);

const shopifyIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'shopify'
);

const isShopifyFeatureEnabled = computed(
  () => shopifyIntegration.value.enabled
);

const { isCloudFeatureEnabled } = useAccount();

const isLinearFeatureEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.LINEAR)
);

const linearIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'linear'
);

const isLinearClientIdConfigured = computed(() => {
  return !!linearIntegration.value?.id;
});

const isLinearConnected = computed(
  () => linearIntegration.value?.enabled || false
);

const store = useStore();
const { t } = useI18n();
const currentChat = useMapGetter('getSelectedChat');
const conversationId = computed(() => props.conversationId);
const conversationMetadataGetter = useMapGetter(
  'conversationMetadata/getConversationMetadata'
);
const currentConversationMetaData = computed(() =>
  conversationMetadataGetter.value(conversationId.value)
);
const conversationAdditionalAttributes = computed(
  () => currentConversationMetaData.value.additional_attributes || {}
);

const channelType = computed(() => currentChat.value.meta?.channel);

const contactGetter = useMapGetter('contacts/getContact');
const contactId = computed(() => currentChat.value.meta?.sender?.id);
const contact = computed(() => contactGetter.value(contactId.value));
const contactAdditionalAttributes = computed(
  () => contact.value.additional_attributes || {}
);

const contactAccountIdentifier = computed(() => contact.value?.email || '');

const contactEmbedContactIdentifier = computed(() => {
  if (contact.value?.email) return '';

  const telegramUserId = contact.value?.telegram_user_id || '';
  if (telegramUserId) return `Telegram/${telegramUserId}`;

  const slackUserId = contact.value?.slack_user_id || '';
  if (slackUserId) return `Slack/${slackUserId}`;

  return '';
});
const contactEmbedUrl = computed(
  () => window.chatwootConfig?.contactSidebarEmbedUrl || ''
);
const contactEmbedMethod = computed(
  () => window.chatwootConfig?.contactSidebarEmbedMethod || 'GET'
);
const isContactEmbedOpen = ref(true);
const isContactAddTicketEmbedOpen = ref(false);
const isContactOrderEmbedOpen = ref(false);
const isContactTicketDetailEmbedOpen = ref(false);
const isContactUserIdEmbedOpen = ref(false);

const activeTabIndex = ref(0);
const tabs = computed(() => [
  { key: 'orders', label: t('CONVERSATION.SIDEBAR.TABS.ORDERS') },
  { key: 'tickets', label: t('CONVERSATION.SIDEBAR.TABS.TICKETS') },
  { key: 'conversation', label: t('CONVERSATION.SIDEBAR.TABS.CONVERSATION') },
]);

const activeTabKey = computed(() => tabs.value[activeTabIndex.value]?.key);
const isOrdersTabActive = computed(() => activeTabKey.value === 'orders');
const isTicketsTabActive = computed(() => activeTabKey.value === 'tickets');

const handleTabChanged = tab => {
  activeTabIndex.value = Math.max(
    0,
    tabs.value.findIndex(item => item.key === tab.key)
  );
};

const conversationUuid = computed(
  () =>
    currentChat.value?.contact_inbox?.source_id || currentChat.value?.uuid || ''
);
const crmAccountId = computed(() => contact.value?.crm_account_id || '');
const crmEmail = computed(() => contact.value?.crm_email || '');

const getContactDetails = () => {
  if (!contactId.value) return Promise.resolve();

  return store.dispatch('contacts/show', { id: contactId.value });
};

const reloadKey = ref(0);
const isRefreshing = ref(false);
const headerButtons = computed(() => [
  {
    key: 'refresh',
    icon: 'i-lucide-refresh-ccw',
    tooltip: t('GENERAL.REFRESH'),
    isLoading: isRefreshing.value,
    disabled: isRefreshing.value,
  },
]);

const handleHeaderAction = async key => {
  if (key !== 'refresh') return;
  if (isRefreshing.value) return;

  isRefreshing.value = true;
  // 刷新时默认切换到订单标签页
  activeTabIndex.value = 0;
  isContactEmbedOpen.value = true;
  isContactAddTicketEmbedOpen.value = false;
  isContactOrderEmbedOpen.value = false;
  isContactTicketDetailEmbedOpen.value = false;
  isContactUserIdEmbedOpen.value = false;
  reloadKey.value += 1;

  try {
    const refreshPromises = [
      getContactDetails(),
      store.dispatch('attributes/get', 0),
      store.dispatch('integrations/get', 'linear'),
    ];
    await Promise.allSettled(refreshPromises);
    useAlert(t('CONVERSATION.SIDEBAR.REFRESH_SUCCESS'));
  } finally {
    isRefreshing.value = false;
  }
};

watch(contactId, (newContactId, prevContactId) => {
  if (newContactId && newContactId !== prevContactId) {
    getContactDetails();
  }
});

const onDragEnd = () => {
  dragging.value = false;
  updateUISettings({
    conversation_sidebar_items_order: conversationSidebarItems.value,
  });
};

const closeContactPanel = () => {
  closeContactSidebar();
  updateUISettings({ is_copilot_panel_open: false });
};

const handleResizeMouseDown = event => {
  if (props.onResizeStart) props.onResizeStart(event);
};

onMounted(() => {
  conversationSidebarItems.value = conversationSidebarItemsOrder.value;
  getContactDetails();
  store.dispatch('attributes/get', 0);
  // Load integrations to ensure linear integration state is available
  store.dispatch('integrations/get', 'linear');
});
</script>

<template>
  <div class="w-full">
    <SidebarActionsHeader
      :title="$t('CONVERSATION.SIDEBAR.CONTACT')"
      :buttons="headerButtons"
      @click="handleHeaderAction"
      @close="closeContactPanel"
    >
      <template #titlePrefix>
        <button
          v-tooltip.bottom="$t('CONVERSATION.SIDEBAR.RESIZE')"
          type="button"
          class="hidden md:flex items-center justify-center size-7 rounded-md bg-n-alpha-black2 text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-black3 cursor-col-resize"
          :aria-label="$t('CONVERSATION.SIDEBAR.RESIZE')"
          @mousedown="handleResizeMouseDown"
        >
          <span class="i-lucide-grip-vertical text-sm" />
        </button>
      </template>
    </SidebarActionsHeader>
    <ContactInfo
      :contact="contact"
      :channel-type="channelType"
      :conversation-uuid="conversationUuid"
    />
    <div class="px-2 pb-8 list-group flex flex-col gap-3">
      <div class="flex justify-center">
        <TabBar
          :tabs="tabs"
          :initial-active-tab="activeTabIndex"
          @tab-changed="handleTabChanged"
        />
      </div>
      <template v-if="isOrdersTabActive">
        <ContactEmbedPanel
          :is-open="isContactEmbedOpen"
          :reload-key="reloadKey"
          :conversation-uuid="conversationUuid"
          :src="contactEmbedUrl"
          :method="contactEmbedMethod"
          :account="contactAccountIdentifier"
          :contact="contactEmbedContactIdentifier"
          @toggle="isContactEmbedOpen = !isContactEmbedOpen"
        />
        <AccordionItem
          v-if="contact.id"
          :title="$t('CONVERSATION_SIDEBAR.ACCORDION.PREVIOUS_CONVERSATION')"
          :is-open="isContactSidebarItemOpen('is_previous_conv_open')"
          compact
          @toggle="value => toggleSidebarUIState('is_previous_conv_open', value)"
        >
          <ContactConversations
            :contact-id="contact.id"
            :conversation-id="conversationId"
          />
        </AccordionItem>
        <ContactOrderEmbedPanel
          :is-open="isContactOrderEmbedOpen"
          :conversation-uuid="conversationUuid"
          :reload-key="reloadKey"
          :crm-account-id="crmAccountId"
          :crm-email="crmEmail"
          @toggle="isContactOrderEmbedOpen = !isContactOrderEmbedOpen"
        />
        <ContactUserIdEmbedPanel
          :is-open="isContactUserIdEmbedOpen"
          :conversation-uuid="conversationUuid"
          :reload-key="reloadKey"
          :crm-account-id="crmAccountId"
          @toggle="isContactUserIdEmbedOpen = !isContactUserIdEmbedOpen"
        />
      </template>
      <template v-else-if="isTicketsTabActive">
        <ContactAddTicketEmbedPanel
          :is-open="isContactAddTicketEmbedOpen"
          :reload-key="reloadKey"
          :crm-account-id="crmAccountId"
          :crm-email="crmEmail"
          @toggle="isContactAddTicketEmbedOpen = !isContactAddTicketEmbedOpen"
        />
        <ContactTicketDetailEmbedPanel
          :is-open="isContactTicketDetailEmbedOpen"
          :reload-key="reloadKey"
          @toggle="
            isContactTicketDetailEmbedOpen = !isContactTicketDetailEmbedOpen
          "
        />
      </template>
      <Draggable
        v-else
        :list="conversationSidebarItems"
        animation="200"
        ghost-class="ghost"
        handle=".drag-handle"
        item-key="name"
        class="flex flex-col gap-3"
        @start="dragging = true"
        @end="onDragEnd"
      >
        <template #item="{ element }">
          <div
            v-if="element.name === 'conversation_actions'"
            class="conversation--actions"
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_ACTIONS')"
              :is-open="isContactSidebarItemOpen('is_conv_actions_open')"
              @toggle="
                value => toggleSidebarUIState('is_conv_actions_open', value)
              "
            >
              <ConversationAction
                :conversation-id="conversationId"
                :inbox-id="inboxId"
              />
            </AccordionItem>
          </div>
          <div
            v-else-if="element.name === 'conversation_participants'"
            class="conversation--actions"
          >
            <AccordionItem
              :title="$t('CONVERSATION_PARTICIPANTS.SIDEBAR_TITLE')"
              :is-open="isContactSidebarItemOpen('is_conv_participants_open')"
              @toggle="
                value =>
                  toggleSidebarUIState('is_conv_participants_open', value)
              "
            >
              <ConversationParticipant
                :conversation-id="conversationId"
                :inbox-id="inboxId"
              />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'conversation_info'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_INFO')"
              :is-open="isContactSidebarItemOpen('is_conv_details_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_conv_details_open', value)
              "
            >
              <ConversationInfo
                :conversation-attributes="conversationAdditionalAttributes"
                :contact-attributes="contactAdditionalAttributes"
              />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'contact_attributes'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_ATTRIBUTES')"
              :is-open="isContactSidebarItemOpen('is_contact_attributes_open')"
              compact
              @toggle="
                value =>
                  toggleSidebarUIState('is_contact_attributes_open', value)
              "
            >
              <CustomAttributes
                attribute-type="contact_attribute"
                attribute-from="conversation_contact_panel"
                :contact-id="contact.id"
                :empty-state-message="
                  $t('CONVERSATION_CUSTOM_ATTRIBUTES.NO_RECORDS_FOUND')
                "
              />
            </AccordionItem>
          </div>
          <woot-feature-toggle
            v-else-if="element.name === 'macros'"
            feature-key="macros"
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.MACROS')"
              :is-open="isContactSidebarItemOpen('is_macro_open')"
              compact
              @toggle="value => toggleSidebarUIState('is_macro_open', value)"
            >
              <MacrosList :conversation-id="conversationId" />
            </AccordionItem>
          </woot-feature-toggle>
          <div
            v-else-if="
              element.name === 'linear_issues' &&
              isLinearFeatureEnabled &&
              isLinearClientIdConfigured
            "
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.LINEAR_ISSUES')"
              :is-open="isContactSidebarItemOpen('is_linear_issues_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_linear_issues_open', value)
              "
            >
              <LinearSetupCTA v-if="!isLinearConnected" />
              <LinearIssuesList v-else :conversation-id="conversationId" />
            </AccordionItem>
          </div>
          <div
            v-else-if="
              element.name === 'shopify_orders' && isShopifyFeatureEnabled
            "
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.SHOPIFY_ORDERS')"
              :is-open="isContactSidebarItemOpen('is_shopify_orders_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_shopify_orders_open', value)
              "
            >
              <ShopifyOrdersList :contact-id="contactId" />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'contact_notes'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_NOTES')"
              :is-open="isContactSidebarItemOpen('is_contact_notes_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_contact_notes_open', value)
              "
            >
              <ContactNotes :contact-id="contactId" />
            </AccordionItem>
          </div>
        </template>
      </Draggable>
    </div>
  </div>
</template>

<style lang="scss" scoped>
::v-deep {
  .contact--profile {
    @apply pb-3 border-b border-solid border-n-weak;
  }

  .conversation--actions .multiselect-wrap--small {
    .multiselect {
      @apply box-border pl-6;
    }

    .multiselect__element {
      span {
        @apply w-full;
      }
    }
  }
}
</style>
